import { MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import { CompanyCredit } from "@/domains/app/components/company-credit";
import { SettingsNavigation } from "@/domains/onboarding/salon/components/settings-navigation";
import type { Salon, WorkerSalon } from "@/gql/graphql";
import { BaseNavigationBar } from "@/lib/components/navigation";
import { PageContent } from "@/lib/components/page-content";
import { flattenSalonsWithFranchise } from "@/lib/components/salon-selector/utils";
import { DashboardMode, useDashboardContext } from "@/lib/dashboard-context";
import { env } from "@/lib/env";
import { AppUpdaterService } from "@/lib/services/app-updater-service";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";
import { AppearanceCard } from "../components/appearance-card";
import { NavigationSection, ProfileSection, SalonSelectionSection } from "../components/sections";
import { filterNavigationItems, getNavigationItems } from "../utils/navigation";

const SUPPORT_WHATSAPP_URL = "https://wa.me/4915231480096";

export function SalonPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("settings");
  const { user, logout } = useAuthStore();
  const { currentSalon, franchiseAccess, setCurrentSalon } = useSalonStore();
  const [currentVersion, setCurrentVersion] = useState<string>("");

  const { mode } = useDashboardContext();

  // Get available salons from franchise access with franchise information
  const availableSalons = flattenSalonsWithFranchise(franchiseAccess);

  // Create userSalonRoles from available salons (they include role information)
  const userSalonRoles = availableSalons.map((salon) => ({
    salonId: salon.id,
    role: salon.role || "EMPLOYEE",
  }));

  const navigationItems = getNavigationItems();
  const secondaryItems = filterNavigationItems(navigationItems, "secondary");

  // Load current version on mount
  useEffect(() => {
    const loadCurrentVersion = async () => {
      try {
        const version = await AppUpdaterService.getInstance().getCurrentVersion();
        setCurrentVersion(version);
      } catch (error) {
        console.error("Failed to load current version:", error);
      }
    };

    loadCurrentVersion();
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success(t("salonPage.messages.loggedOutSuccessfully"));
  };

  const handleProfileClick = () => {
    navigate("/settings/profile");
  };

  const handleSalonChange = (salonId: string) => {
    const selectedSalon = availableSalons.find((salon) => salon.id === salonId);
    if (selectedSalon) {
      setCurrentSalon(selectedSalon);
      toast.success(
        t("salonPage.messages.switchedToSalon", {
          salonName: selectedSalon.name,
        })
      );
    }
  };

  const handleTestToast = () => {
    toast.success("Testing toast safe area - this should appear below the status bar on mobile!");
  };

  return (
    <>
      <BaseNavigationBar title={t("salonPage.title")} subtitle={t("salonPage.subtitle")} />

      <PageContent>
        <div className="space-y-4">
          {/* Salon Information Card */}
          {currentSalon && (
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <SalonSelectionSection
                currentSalon={currentSalon as unknown as Salon}
                availableSalons={availableSalons as unknown as Salon[]}
                userSalonRoles={userSalonRoles as unknown as WorkerSalon[]}
                onSalonChange={handleSalonChange}
              />
            </div>
          )}

          {/* Development Testing Card */}
          {!env.isProd && (
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <h3 className="text-lg font-semibold mb-3">Development Testing</h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Test toast notifications for mobile safe area positioning
                </p>
                <Button onClick={handleTestToast} variant="outline" size="sm">
                  Test Toast
                </Button>
              </div>
            </div>
          )}

          {/* Appearance Settings Card */}
          <AppearanceCard />

          {/* Tools Card - includes Services and Reports */}
          {secondaryItems.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <NavigationSection
                title={t("salonPage.sections.tools")}
                items={secondaryItems}
                onNavigation={handleNavigation}
              />
            </div>
          )}

          {/* Settings Card */}
          {mode === DashboardMode.MANAGER && (
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <SettingsNavigation variant="salon" />
            </div>
          )}

          {/* Account Card */}
          {user && (
            <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
              <ProfileSection
                user={user}
                currentSalon={currentSalon as Salon | null}
                onProfileClick={handleProfileClick}
                onLogout={handleLogout}
              />
            </div>
          )}

          {/* Contact Support Card */}
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <Button asChild variant="outline" size="lg" className="w-full h-12 text-lg">
              <a
                href={SUPPORT_WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3"
              >
                <MessageCircle className="w-6 h-6" />
                <span>{t("contactSupport")}</span>
              </a>
            </Button>
          </div>

          {/* App Version Card */}
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <h3 className="text-lg font-semibold mb-3">App Version</h3>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Current Capacitor version:
                <span className="ml-2 font-mono text-foreground">
                  {currentVersion || "Loading..."}
                </span>
              </p>
            </div>
          </div>

          {/* Logo at bottom */}
          <div className="flex justify-center py-2">
            <Logo width={300} className="max-w-full" />
          </div>
          <CompanyCredit className="px-4 pb-4" />
        </div>
      </PageContent>
    </>
  );
}
